import { useEffect } from 'react';
import { useMediaQuery } from '../../common/responsive';

interface ScrollRestoreProps {
  /** Scroll behavior - 'auto' for instant, 'smooth' for animated */
  behavior?: ScrollBehavior;
  /** Target scroll position (defaults to 0 for top) */
  top?: number;
  /** Whether to scroll on mount (defaults to true) */
  scrollOnMount?: boolean;
  /** Optional dependencies that will trigger a scroll when changed */
  dependencies?: any[];
}

/**
 * A component that handles scroll restoration
 *
 * @example
 * // Basic usage - scrolls to top on mount
 * <ScrollRestore />
 *
 * @example
 * // Smooth scroll to specific position
 * <ScrollRestore behavior="smooth" top={100} />
 *
 * @example
 * // Scroll when dependencies change
 * <ScrollRestore dependencies={[someValue]} />
 */
const ScrollRestore = ({
  behavior = 'auto',
  top = 0,
  scrollOnMount = true,
  dependencies = [],
}: ScrollRestoreProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    if (scrollOnMount) {
      if (isMobile) {
        const ionContent = document.querySelector('ion-content');
        if (ionContent) {
          if (top === 0 && ionContent.scrollToTop) {
            ionContent.scrollToTop(behavior === 'smooth' ? 300 : 0);
          } else if (ionContent.scrollTo) {
            ionContent.scrollTo({
              top,
              left: 0,
              behavior: behavior,
            });
          } else {
            ionContent.scrollTop = top;
          }
        } else {
          window.scrollTo({
            top,
            behavior,
          });
        }
      } else {
        window.scrollTo({
          top,
          behavior,
        });
      }
    }
  }, [behavior, top, scrollOnMount, isMobile, ...dependencies]);

  return null;
};

export default ScrollRestore;
